import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { CRM_ACTIVITY_TYPES } from '@bcc/shared';
import type { CrmActivity, CrmContactWithMeta } from '@bcc/shared';
import type { Deal } from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_PANEL } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatPhoneForDisplay } from '../../lib/phoneIntl';

/** Single counterpart workspace — dossier + communications + underwriting bridge. */

export function CrmContactDetail() {
  const { id } = useParams();
  const { session } = useAuth();
  const token = session?.access_token;

  const [contact, setContact] = useState<CrmContactWithMeta | null>(null);
  const [activities, setActivities] = useState<CrmActivity[]>([]);
  const [dealsList, setDealsList] = useState<Deal[]>([]);
  const [dealPick, setDealPick] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const [atype, setAtype] = useState<(typeof CRM_ACTIVITY_TYPES)[keyof typeof CRM_ACTIVITY_TYPES]>(
    CRM_ACTIVITY_TYPES.NOTE
  );
  const [acontent, setAcontent] = useState('');

  useEffect(() => {
    if (!token || !id) return;
    void (async () => {
      const [c, acts, dl] = await Promise.all([
        apiFetch<{ contact: CrmContactWithMeta }>(`/api/crm/contacts/${id}`, { token }),
        apiFetch<{ activities: CrmActivity[] }>(`/api/crm/contacts/${id}/activities`, { token }),
        apiFetch<{ deals: Deal[] }>('/api/deals', { token }),
      ]);
      setContact(c.contact);
      setNotesDraft(c.contact.notes ?? '');
      setActivities(acts.activities);
      setDealsList(dl.deals);
    })();
  }, [token, id]);

  async function saveNotes(e: FormEvent) {
    e.preventDefault();
    if (!token || !id) return;
    setSavingNotes(true);
    await apiFetch(`/api/crm/contacts/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ notes: notesDraft }),
    });
    const c = await apiFetch<{ contact: CrmContactWithMeta }>(`/api/crm/contacts/${id}`, { token });
    setContact(c.contact);
    setSavingNotes(false);
  }

  async function submitActivity(e: FormEvent) {
    e.preventDefault();
    if (!token || !id || !acontent.trim()) return;
    await apiFetch(`/api/crm/contacts/${id}/activities`, {
      method: 'POST',
      token,
      body: JSON.stringify({ type: atype, content: acontent }),
    });
    const acts = await apiFetch<{ activities: CrmActivity[] }>(`/api/crm/contacts/${id}/activities`, { token });
    setActivities(acts.activities);
    setAcontent('');
  }

  async function attachDeal(e: FormEvent) {
    e.preventDefault();
    if (!dealPick || !token || !id) return;
    await apiFetch(`/api/crm/contacts/${id}/deals`, {
      method: 'POST',
      token,
      body: JSON.stringify({ deal_id: dealPick }),
    });
    const c = await apiFetch<{ contact: CrmContactWithMeta }>(`/api/crm/contacts/${id}`, { token });
    setContact(c.contact);
    setDealPick('');
  }

  async function detachDeal(dealId: string) {
    if (!token || !id) return;
    await apiFetch(`/api/crm/contacts/${id}/deals/${dealId}`, { method: 'DELETE', token });
    const c = await apiFetch<{ contact: CrmContactWithMeta }>(`/api/crm/contacts/${id}`, { token });
    setContact(c.contact);
  }

  if (!contact) {
    return <p className="text-sm text-[var(--home-muted-on-dark)]">Opening dossier…</p>;
  }

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <Link to="/admin/crm/contacts" className="text-[var(--home-muted-on-dark)] hover:text-[var(--home-miami-blue)]">
          ← Contacts
        </Link>
      </div>

      <header className={`${SITE_GLASS_PANEL} p-5 md:p-6`}>
        <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">{contact.full_name}</h1>
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          <span className="rounded-full border border-white/[0.12] bg-black/25 px-3 py-1 capitalize ring-1 ring-white/[0.06]">
            {contact.type}
          </span>
          <span className="rounded-full border border-[var(--home-miami-blue)]/35 bg-[var(--home-miami-blue)]/22 px-3 py-1 capitalize text-[var(--home-miami-blue)] ring-1 ring-white/[0.08]">
            {contact.status}
          </span>
          {contact.lead_source ? (
            <span className="rounded-full border border-white/[0.12] bg-black/25 px-3 py-1 capitalize ring-1 ring-white/[0.06]">
              Src: {String(contact.lead_source).replace('_', ' ')}
            </span>
          ) : null}
        </div>
        <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-[11px] uppercase text-[var(--home-muted-on-dark)]">Email</dt>
            <dd className="text-[var(--home-text-on-dark)]">{contact.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase text-[var(--home-muted-on-dark)]">Phone</dt>
            <dd className="text-[var(--home-text-on-dark)]">{formatPhoneForDisplay(contact.phone) || '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase text-[var(--home-muted-on-dark)]">Desired commitment</dt>
            <dd className="text-[var(--home-text-on-dark)]">{contact.desired_commitment?.trim() ? contact.desired_commitment : '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase text-[var(--home-muted-on-dark)]">Accredited investor</dt>
            <dd className="text-[var(--home-text-on-dark)]">
              {contact.accredited_investor === true ? 'Yes' : contact.accredited_investor === false ? 'No' : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase text-[var(--home-muted-on-dark)]">Owner</dt>
            <dd className="text-[var(--home-text-on-dark)]">{contact.assigned_name ?? 'Unassigned'}</dd>
          </div>
        </dl>
      </header>

      <section className="grid gap-6 lg:grid-cols-2 lg:gap-8">
        <div className={`${SITE_GLASS_PANEL} space-y-4 p-5 md:p-6`}>
          <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">
            Master notes
          </h2>
          <form className="space-y-3" onSubmit={saveNotes}>
            <textarea
              rows={8}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              className={`w-full bg-white/[0.14] leading-relaxed ${SITE_FORM_CONTROL} px-4 py-3`}
              placeholder="# Markdown-supported narrative…"
            />
            <button
              type="submit"
              disabled={savingNotes}
              className="rounded-xl border border-white/[0.16] bg-black/30 px-5 py-2 text-sm text-[var(--home-text-on-dark)] shadow-md shadow-black/20 transition hover:bg-black/40 disabled:opacity-50"
            >
              {savingNotes ? 'Persisting…' : 'Persist notes'}
            </button>
          </form>
          <article
            className="space-y-3 rounded-2xl border border-white/[0.1] bg-black/28 p-5 text-sm leading-relaxed text-[var(--home-text-on-dark)]/95 shadow-inner shadow-black/20 [&_p]:my-2 [&_strong]:text-[var(--home-text-on-dark)] [&_li]:my-1"
          >
            <ReactMarkdown>{notesDraft || '*No narrative yet.*'}</ReactMarkdown>
          </article>
        </div>

        <div className="space-y-6">
          <div className={`${SITE_GLASS_PANEL} p-5 md:p-6`}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">
              Linked underwriting
            </h2>
            <ul className="mb-4 space-y-2 text-sm">
              {(contact.deals ?? []).length === 0 ? (
                <li className="text-[var(--home-muted-on-dark)]">Nothing linked.</li>
              ) : (
                (contact.deals ?? []).map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-4">
                    <span className="text-[var(--home-text-on-dark)]">{d.title}</span>
                    <button
                      type="button"
                      className="text-[11px] text-red-300 hover:text-red-200"
                      onClick={() => void detachDeal(d.id)}
                    >
                      Unlink
                    </button>
                  </li>
                ))
              )}
            </ul>
            <form className="flex flex-wrap gap-2" onSubmit={attachDeal}>
              <select
                value={dealPick}
                onChange={(e) => setDealPick(e.target.value)}
                className={`min-w-[200px] flex-1 bg-white/[0.14] text-xs capitalize ${SITE_FORM_CONTROL}`}
              >
                <option value="">Select offering…</option>
                {dealsList.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-black/25 hover:bg-emerald-500"
              >
                Link deal
              </button>
            </form>
          </div>

          <div className={`${SITE_GLASS_PANEL} p-5 md:p-6`}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">
              Log activity
            </h2>
            <form className="space-y-3" onSubmit={submitActivity}>
              <div className="flex flex-wrap gap-2">
                <select
                  value={atype}
                  onChange={(e) =>
                    setAtype(e.target.value as (typeof CRM_ACTIVITY_TYPES)[keyof typeof CRM_ACTIVITY_TYPES])
                  }
                  className={`bg-white/[0.14] text-xs capitalize ${SITE_FORM_CONTROL}`}
                >
                  {Object.values(CRM_ACTIVITY_TYPES).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                required
                rows={4}
                value={acontent}
                onChange={(e) => setAcontent(e.target.value)}
                className={`w-full bg-white/[0.14] text-xs ${SITE_FORM_CONTROL}`}
              />
              <button
                type="submit"
                className="rounded-xl bg-[var(--home-miami-blue)] px-4 py-2 text-xs font-semibold text-[var(--home-zone-void)] shadow-md shadow-black/25 transition hover:brightness-105"
              >
                Record touchpoint
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className={`${SITE_GLASS_PANEL} p-5 md:p-6`}>
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">
          Activity chronology
        </h2>
        <div className="ml-3 space-y-6 border-l border-white/15 pl-6">
          {activities.map((a) => (
            <article key={a.id} className="relative">
              <span className="absolute -left-[calc(1.5rem+5px)] top-2 h-2.5 w-2.5 rounded-full bg-[var(--home-miami-blue)] shadow-[0_0_14px_rgba(84_195_234/0.45)]" />
              <p className="text-[11px] uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                {new Date(a.created_at).toLocaleString()} · {a.type}
              </p>
              <div className="mt-2 rounded-xl border border-white/[0.08] bg-black/22 px-3 py-2.5 whitespace-pre-wrap text-sm text-[var(--home-text-on-dark)]">
                {a.content}
              </div>
            </article>
          ))}
          {activities.length === 0 ? (
            <p className="text-xs text-[var(--home-muted-on-dark)]">No logged interactions yet.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
